import { useState, useEffect, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Package,
  Plus,
  Search,
  Edit2,
  Trash2,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  ArrowUpDown,
  BarChart3,
  ShoppingBag,
  Boxes,
  Calculator,
  BookOpen,
  Download,
  Filter,
  PackagePlus,
  XCircle,
  CheckCircle2,
  UserCheck,
  Scissors,
  ClipboardList,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { RegisterUsageModal } from "@/components/RegisterUsageModal";

const CATEGORIES = ["Shampoo", "Condicionador", "Tintura", "Tratamento", "Acessório", "Cosmético", "Equipamento", "Outro"];
const UNITS = ["un", "ml", "L", "g", "kg", "cx"];

type Product = {
  id: number;
  name: string;
  description?: string | null;
  category?: string | null;
  brand?: string | null;
  sku?: string | null;
  costPrice: string;
  profitMargin: string;
  salePrice: string;
  stockQty: string;
  stockMin: string;
  unit?: string | null;
  imageUrl?: string | null;
  isActive: boolean;
  isInternalUse?: boolean | null;
  deductFromCommission?: boolean | null;
  professionalUseCost?: string | null;
  internalUseCost?: string | null;
  contentQty?: string | null;
  createdAt: Date;
  updatedAt: Date;
};

const fmt = (v: string | number) => parseFloat(String(v)).toFixed(2);
const fmtCurrency = (v: string | number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(parseFloat(String(v)));

// ─── Formulário de Produto ────────────────────────────────────────────────────
function ProductForm({
  initial,
  onSave,
  onCancel,
  isLoading,
}: {
  initial?: Partial<Product>;
  onSave: (data: {
    name: string; description?: string; category?: string; brand?: string; sku?: string;
    costPrice: number; profitMargin: number; salePrice: number;
    stockQty?: number; stockMin?: number; unit?: string; imageUrl?: string;
    isInternalUse?: boolean; deductFromCommission?: boolean;
    professionalUseCost?: number; internalUseCost?: number;
    contentQty?: number | null;
  }) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [category, setCategory] = useState(initial?.category ?? "");
  const [showCustomCategory, setShowCustomCategory] = useState(
    !!(initial?.category && !CATEGORIES.includes(initial.category))
  );
  const [brand, setBrand] = useState(initial?.brand ?? "");
  const [sku, setSku] = useState(initial?.sku ?? "");
  const [costPrice, setCostPrice] = useState(initial?.costPrice ? fmt(initial.costPrice) : "0.00");
  const [profitMargin, setProfitMargin] = useState(initial?.profitMargin ? fmt(initial.profitMargin) : "30.00");
  const [salePrice, setSalePrice] = useState(initial?.salePrice ? fmt(initial.salePrice) : "0.00");
  const [stockQty, setStockQty] = useState(initial?.stockQty ? fmt(initial.stockQty) : "0");
  const [stockMin, setStockMin] = useState(initial?.stockMin ? fmt(initial.stockMin) : "0");
  const [unit, setUnit] = useState(initial?.unit ?? "un");
  const [imageUrl, setImageUrl] = useState(initial?.imageUrl ?? "");
  const [imageUploading, setImageUploading] = useState(false);
  const [autoCalc, setAutoCalc] = useState(true);
  const [isInternalUse, setIsInternalUse] = useState(initial?.isInternalUse ?? false);
  const [deductFromCommission, setDeductFromCommission] = useState(initial?.deductFromCommission ?? false);
  const [professionalUseCost, setProfessionalUseCost] = useState(initial?.professionalUseCost ? fmt(initial.professionalUseCost) : "0.00");
  const [internalUseCost, setInternalUseCost] = useState(initial?.internalUseCost ? fmt(initial.internalUseCost) : "0.00");
  const [contentQty, setContentQty] = useState(initial?.contentQty ? String(parseFloat(initial.contentQty)) : "");
  const [autoCalcUsage, setAutoCalcUsage] = useState(false);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Imagem muito grande. Máximo 5MB."); return; }
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await fetch("/api/products/upload-image", { method: "POST", body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Erro no upload");
      setImageUrl(data.url);
      toast.success("Foto enviada com sucesso!");
    } catch (err: any) {
      toast.error(err.message ?? "Erro ao enviar foto");
    } finally {
      setImageUploading(false);
    }
  };

  // Cálculo automático do preço de venda
  useEffect(() => {
    if (!autoCalc) return;
    const cost = parseFloat(costPrice) || 0;
    const margin = parseFloat(profitMargin) || 0;
    const calculated = cost * (1 + margin / 100);
    setSalePrice(calculated.toFixed(2));
  }, [costPrice, profitMargin, autoCalc]);

  // Cálculo automático dos valores de uso (sugestão = preço de venda / quantidade do produto)
  useEffect(() => {
    if (!autoCalcUsage) return;
    const sale = parseFloat(salePrice) || 0;
    const qty = parseFloat(contentQty) || 0;
    if (sale > 0 && qty > 0) {
      const perUnit = sale / qty;
      setProfessionalUseCost(perUnit.toFixed(2));
      setInternalUseCost(perUnit.toFixed(2));
    }
  }, [salePrice, contentQty, autoCalcUsage]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Nome do produto é obrigatório"); return; }
    onSave({
      name: name.trim(),
      description: description || undefined,
      category: category || undefined,
      brand: brand || undefined,
      sku: sku || undefined,
      costPrice: parseFloat(costPrice) || 0,
      profitMargin: parseFloat(profitMargin) || 0,
      salePrice: parseFloat(salePrice) || 0,
      stockQty: initial ? undefined : parseFloat(stockQty) || 0,
      stockMin: parseFloat(stockMin) || 0,
      unit,
      imageUrl: imageUrl || undefined,
      isInternalUse,
      deductFromCommission,
      professionalUseCost: parseFloat(professionalUseCost) || 0,
      internalUseCost: parseFloat(internalUseCost) || 0,
      contentQty: contentQty ? parseFloat(contentQty) || null : null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2 space-y-1.5">
          <Label>Nome do Produto *</Label>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Shampoo Hidratante 300ml" required />
        </div>
        <div className="space-y-1.5">
          <Label>Categoria</Label>
          {showCustomCategory ? (
            <div className="flex gap-2">
              <Input
                value={category}
                onChange={e => setCategory(e.target.value)}
                placeholder="Digite a categoria..."
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-xs px-2"
                onClick={() => { setShowCustomCategory(false); setCategory(""); }}
              >
                Lista
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="flex-1"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem categoria</SelectItem>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-xs px-2 whitespace-nowrap"
                onClick={() => { setShowCustomCategory(true); setCategory(""); }}
              >
                + Nova
              </Button>
            </div>
          )}
        </div>
        <div className="space-y-1.5">
          <Label>Marca</Label>
          <Input value={brand} onChange={e => setBrand(e.target.value)} placeholder="Ex: L'Oréal" />
        </div>
        <div className="space-y-1.5">
          <Label>SKU / Código</Label>
          <Input value={sku} onChange={e => setSku(e.target.value)} placeholder="Código interno" />
        </div>
        <div className="space-y-1.5">
          <Label>Unidade</Label>
          <Select value={unit} onValueChange={setUnit}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Precificação */}
      <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <Calculator className="h-4 w-4 text-primary" />
            Precificação
          </h4>
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={autoCalc}
              onChange={e => setAutoCalc(e.target.checked)}
              className="rounded"
            />
            Calcular automaticamente
          </label>
        </div>
        {/* Quantidade do produto (capacidade) */}
        <div className="space-y-1.5">
          <Label className="text-sm">Quantidade do Produto ({unit})</Label>
          <p className="text-xs text-muted-foreground">Capacidade total do produto (ex: 300 para um frasco de 300{unit}). Usado para calcular o custo por {unit} de uso.</p>
          <Input
            type="number" step="0.001" min="0"
            value={contentQty}
            onChange={e => setContentQty(e.target.value)}
            placeholder={`Ex: 300 (para 300${unit})`}
          />
          {contentQty && parseFloat(contentQty) > 0 && parseFloat(salePrice) > 0 && (
            <p className="text-xs text-primary font-medium">
              ≈ R$ {(parseFloat(salePrice) / parseFloat(contentQty)).toFixed(4)} por {unit}
            </p>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label>Preço de Custo (R$)</Label>
            <Input
              type="number" step="0.01" min="0"
              value={costPrice}
              onChange={e => setCostPrice(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Margem de Lucro (%)</Label>
            <Input
              type="number" step="0.1" min="0" max="1000"
              value={profitMargin}
              onChange={e => setProfitMargin(e.target.value)}
              placeholder="30"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Preço de Venda (R$)</Label>
            <Input
              type="number" step="0.01" min="0"
              value={salePrice}
              onChange={e => { setAutoCalc(false); setSalePrice(e.target.value); }}
              placeholder="0.00"
              className={autoCalc ? "bg-primary/5 border-primary/30" : ""}
            />
            {autoCalc && (
              <p className="text-xs text-primary">
                = R$ {fmt(costPrice)} × (1 + {profitMargin}%)
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Estoque */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {!initial && (
          <div className="space-y-1.5">
            <Label>Estoque Inicial</Label>
            <Input type="number" step="0.001" min="0" value={stockQty} onChange={e => setStockQty(e.target.value)} />
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Estoque Mínimo (alerta)</Label>
          <Input type="number" step="0.001" min="0" value={stockMin} onChange={e => setStockMin(e.target.value)} />
        </div>
      </div>

      {/* Foto do Produto */}
      <div className="space-y-1.5">
        <Label>Foto do Produto</Label>
        <div className="flex items-center gap-3">
          {imageUrl ? (
            <div className="relative">
              <img src={imageUrl} alt="Foto" className="w-16 h-16 rounded-lg object-cover border" />
              <button
                type="button"
                onClick={() => setImageUrl("")}
                className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs"
              >×</button>
            </div>
          ) : (
            <div className="w-16 h-16 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center bg-muted/20">
              <Package className="h-6 w-6 text-muted-foreground/40" />
            </div>
          )}
          <div className="flex-1">
            <label className="cursor-pointer">
              <div className="flex items-center gap-2 px-3 py-2 border rounded-md text-sm hover:bg-muted/50 transition-colors w-fit">
                {imageUploading ? (
                  <><span className="animate-spin">&#8987;</span> Enviando...</>
                ) : (
                  <><span>📷</span> {imageUrl ? "Trocar foto" : "Adicionar foto"}</>
                )}
              </div>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleImageUpload}
                disabled={imageUploading}
              />
            </label>
            <p className="text-xs text-muted-foreground mt-1">JPG, PNG ou WebP • Máx. 5MB</p>
            <div className="mt-2 rounded-lg border border-primary/20 bg-primary/5 p-2.5 text-xs text-muted-foreground">
              <p className="font-semibold text-foreground">Formato ideal para a vitrine</p>
              <p className="mt-1">
                Use foto <strong>vertical 4:5</strong> — recomendado <strong>1080 × 1350 px</strong> (ou 800 × 1000 px).
              </p>
              <p className="mt-1">
                Deixe o produto centralizado, com uma pequena margem ao redor. Fotos quadradas ou horizontais podem sofrer corte no card do celular.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Descrição</Label>
        <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Descrição opcional do produto..." rows={2} />
      </div>

      {/* Uso Interno / Desconto do Profissional */}
      <div className="border rounded-lg p-4 space-y-3 bg-blue-50/40 border-blue-200">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold flex items-center gap-2 text-blue-800">
            <span>🏪</span>
            Uso Interno do Salão
          </h4>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isInternalUse}
            onChange={e => setIsInternalUse(e.target.checked)}
            className="rounded"
          />
          <span className="text-sm font-medium">Produto de uso interno (não aparece na loja)</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={deductFromCommission}
            onChange={e => setDeductFromCommission(e.target.checked)}
            className="rounded"
          />
          <span className="text-sm font-medium">Descontar Produto do Profissional</span>
        </label>
        {deductFromCommission && (
          <>
            {/* Checkbox de cálculo automático */}
            <div className="flex items-center justify-between">
              <p className="text-xs text-blue-700">Valores descontados da comissão por uso do produto.</p>
              <label className="flex items-center gap-2 text-xs text-blue-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoCalcUsage}
                  onChange={e => setAutoCalcUsage(e.target.checked)}
                  className="rounded"
                />
                Calcular automaticamente
              </label>
            </div>
            {autoCalcUsage && contentQty && parseFloat(contentQty) > 0 && parseFloat(salePrice) > 0 && (
              <p className="text-xs text-blue-600 bg-blue-100 rounded px-2 py-1">
                💡 Sugestão: R$ {(parseFloat(salePrice) / parseFloat(contentQty)).toFixed(4)}/{unit} × quantidade usada
                = R$ {(parseFloat(salePrice) / parseFloat(contentQty)).toFixed(2)} por {unit}
              </p>
            )}
            {autoCalcUsage && (!contentQty || parseFloat(contentQty) <= 0) && (
              <p className="text-xs text-amber-600 bg-amber-50 rounded px-2 py-1">
                ⚠️ Preencha a &quot;Quantidade do Produto&quot; na seção de Precificação para calcular automaticamente.
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Uso próprio (R$)</Label>
                <p className="text-xs text-muted-foreground">Produto vendido para o profissional</p>
                <Input
                  type="number" step="0.01" min="0"
                  value={professionalUseCost}
                  onChange={e => { setAutoCalcUsage(false); setProfessionalUseCost(e.target.value); }}
                  placeholder="0.00"
                  className={autoCalcUsage ? "bg-blue-50 border-blue-300" : ""}
                />
                {autoCalcUsage && contentQty && parseFloat(contentQty) > 0 && parseFloat(salePrice) > 0 && (
                  <p className="text-xs text-blue-600">
                    = R$ {parseFloat(salePrice).toFixed(2)} ÷ {parseFloat(contentQty)}{unit}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Uso interno (R$)</Label>
                <p className="text-xs text-muted-foreground">Utilizado na realização de algum serviço</p>
                <Input
                  type="number" step="0.01" min="0"
                  value={internalUseCost}
                  onChange={e => { setAutoCalcUsage(false); setInternalUseCost(e.target.value); }}
                  placeholder="0.00"
                  className={autoCalcUsage ? "bg-blue-50 border-blue-300" : ""}
                />
                {autoCalcUsage && contentQty && parseFloat(contentQty) > 0 && parseFloat(salePrice) > 0 && (
                  <p className="text-xs text-blue-600">
                    = R$ {parseFloat(salePrice).toFixed(2)} ÷ {parseFloat(contentQty)}{unit}
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? "Salvando..." : initial ? "Salvar Alterações" : "Cadastrar Produto"}
        </Button>
      </DialogFooter>
    </form>
  );
}

// ─── Modal de Movimentação de Estoque ────────────────────────────────────────
function StockMovementModal({
  product,
  onClose,
}: {
  product: Product;
  onClose: () => void;
}) {
  const [type, setType] = useState<"in" | "out" | "adjustment">("in");
  const [branchId, setBranchId] = useState("none");
  const [qty, setQty] = useState("1");
  const [unitCost, setUnitCost] = useState(product.costPrice);
  const [unitPrice, setUnitPrice] = useState(product.salePrice);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const utils = trpc.useUtils();
  const { data: branches = [] } = (trpc as any).branches.list.useQuery();

  const addMovement = trpc.products.addMovement.useMutation({
    onSuccess: () => {
      toast.success("Movimentação registrada com sucesso!");
      utils.products.list.invalidate();
      utils.products.getMovements.invalidate();
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addMovement.mutate({
      productId: product.id,
      type,
      qty: parseFloat(qty) || 0,
      unitCost: type === "in" ? parseFloat(unitCost) : undefined,
      unitPrice: type === "out" ? parseFloat(unitPrice) : undefined,
      branchId: branchId !== "none" ? Number(branchId) : undefined,
      reason: reason || undefined,
      notes: notes || undefined,
    });
  };

  const typeLabels = { in: "Entrada (Compra)", out: "Saída (Venda)", adjustment: "Ajuste de Estoque" };
  const typeColors = { in: "text-green-400", out: "text-red-400", adjustment: "text-yellow-400" };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpDown className="h-5 w-5" />
            Movimentação de Estoque
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{product.name}</p>
          <p className="text-sm">
            Estoque atual: <strong>{fmt(product.stockQty)} {product.unit}</strong>
          </p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Tipo de Movimentação</Label>
            <Select value={type} onValueChange={v => setType(v as "in" | "out" | "adjustment")}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="in">📦 Entrada (Compra / Recebimento)</SelectItem>
                <SelectItem value="out">🛒 Saída (Venda / Consumo)</SelectItem>
                <SelectItem value="adjustment">⚖️ Ajuste de Estoque</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Quantidade ({product.unit})</Label>
            <Input type="number" step="0.001" min="0.001" value={qty} onChange={e => setQty(e.target.value)} required />
          </div>
          {branches.length > 0 && <div className="space-y-1.5"><Label>Filial responsável <span className="font-normal text-muted-foreground">(opcional)</span></Label><Select value={branchId} onValueChange={setBranchId}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Sem atribuir a uma filial</SelectItem>{branches.map((branch: any) => <SelectItem key={branch.id} value={String(branch.id)}>{branch.name}</SelectItem>)}</SelectContent></Select><p className="text-xs text-muted-foreground">Ao selecionar uma filial, o custo da saída passa a compor a rentabilidade dela.</p></div>}
          {type === "in" && (
            <div className="space-y-1.5">
              <Label>Custo Unitário (R$)</Label>
              <Input type="number" step="0.01" min="0" value={unitCost} onChange={e => setUnitCost(e.target.value)} />
            </div>
          )}
          {type === "out" && (
            <div className="space-y-1.5">
              <Label>Preço de Venda Unitário (R$)</Label>
              <Input type="number" step="0.01" min="0" value={unitPrice} onChange={e => setUnitPrice(e.target.value)} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Motivo</Label>
            <Input value={reason} onChange={e => setReason(e.target.value)} placeholder={type === "in" ? "Ex: Compra fornecedor" : type === "out" ? "Ex: Venda balcão" : "Ex: Inventário"} />
          </div>
          <div className="space-y-1.5">
            <Label>Observações</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Observações opcionais..." />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={addMovement.isPending}>
              {addMovement.isPending ? "Registrando..." : "Registrar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Card de Produto ──────────────────────────────────────────────────────────
function ProductCard({
  product,
  onEdit,
  onDelete,
  onMovement,
  onUsage,
}: {
  product: Product;
  onEdit: () => void;
  onDelete: () => void;
  onMovement: () => void;
  onUsage?: () => void;
}) {
  const stockQty = parseFloat(product.stockQty);
  const stockMin = parseFloat(product.stockMin);
  const isLowStock = stockQty <= stockMin;
  const margin = parseFloat(product.profitMargin);

  return (
    <Card className={`transition-all hover:shadow-md ${isLowStock ? "border-orange-500/40" : ""}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          {/* Foto */}
          {product.imageUrl ? (
            <img src={product.imageUrl} alt={product.name} className="w-14 h-14 rounded-lg object-cover border shrink-0" />
          ) : (
            <div className="w-14 h-14 rounded-lg border bg-muted/30 flex items-center justify-center shrink-0">
              <Package className="h-6 w-6 text-muted-foreground/40" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-sm truncate">{product.name}</h3>
              {isLowStock && (
                <Badge variant="outline" className="text-orange-400 border-orange-400/50 text-xs shrink-0">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Estoque baixo
                </Badge>
              )}
              {product.isInternalUse && (
                <Badge variant="outline" className="text-amber-500 border-amber-500/50 text-xs shrink-0">
                  <Scissors className="h-3 w-3 mr-1" />
                  Uso Interno
                </Badge>
              )}
            </div>
            {product.brand && <p className="text-xs text-muted-foreground">{product.brand}</p>}
            {product.category && (
              <Badge variant="secondary" className="text-xs mt-1">{product.category}</Badge>
            )}
          </div>
        </div>

        {/* Precificação */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-muted/40 rounded-md p-2">
            <p className="text-xs text-muted-foreground">Custo</p>
            <p className="text-sm font-medium">{fmtCurrency(product.costPrice)}</p>
          </div>
          <div className="bg-primary/10 rounded-md p-2">
            <p className="text-xs text-muted-foreground">Margem</p>
            <p className="text-sm font-semibold text-primary">{margin.toFixed(1)}%</p>
          </div>
          <div className="bg-green-500/10 rounded-md p-2">
            <p className="text-xs text-muted-foreground">Venda</p>
            <p className="text-sm font-semibold text-green-400">{fmtCurrency(product.salePrice)}</p>
          </div>
        </div>

        {/* Estoque */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Estoque:</span>
          <span className={`font-medium ${isLowStock ? "text-orange-400" : "text-foreground"}`}>
            {fmt(product.stockQty)} {product.unit}
            {stockMin > 0 && <span className="text-xs text-muted-foreground ml-1">(mín: {fmt(product.stockMin)})</span>}
          </span>
        </div>

        {/* Ações */}
        <div className="flex gap-2 pt-1">
          <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={onMovement}>
            <ArrowUpDown className="h-3.5 w-3.5 mr-1" />
            Estoque
          </Button>
          {product.deductFromCommission && onUsage && (
            <Button size="sm" variant="outline" className="h-8 text-xs text-amber-600 border-amber-500/40 hover:bg-amber-500/10 px-2" onClick={onUsage}>
              <Scissors className="h-3.5 w-3.5 mr-1" />
              Uso
            </Button>
          )}
          <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={onEdit}>
            <Edit2 className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline" className="h-8 w-8 p-0 text-destructive hover:text-destructive" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Modal Entrada em Lote ──────────────────────────────────────────────────
type BulkRow = { productId: number; type: "in" | "out" | "adjustment"; qty: number; reason: string; };

function BulkMovementModal({
  products, onClose, onSave, isLoading
}: {
  products: Product[];
  onClose: () => void;
  onSave: (movements: BulkRow[]) => void;
  isLoading: boolean;
}) {
  const [rows, setRows] = useState<BulkRow[]>([{ productId: products[0]?.id ?? 0, type: "in", qty: 1, reason: "" }]);

  const addRow = () => setRows(r => [...r, { productId: products[0]?.id ?? 0, type: "in", qty: 1, reason: "" }]);
  const removeRow = (i: number) => setRows(r => r.filter((_, idx) => idx !== i));
  const updateRow = (i: number, patch: Partial<BulkRow>) => setRows(r => r.map((row, idx) => idx === i ? { ...row, ...patch } : row));

  const handleSave = () => {
    const valid = rows.filter(r => r.productId && r.qty > 0);
    if (!valid.length) { return; }
    onSave(valid);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackagePlus className="h-5 w-5" />
            Entrada em Lote
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {rows.map((row, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-5">
                <Select value={String(row.productId)} onValueChange={v => updateRow(i, { productId: Number(v) })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Produto" /></SelectTrigger>
                  <SelectContent>
                    {products.map(p => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Select value={row.type} onValueChange={v => updateRow(i, { type: v as BulkRow["type"] })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Entrada</SelectItem>
                    <SelectItem value="out">Saída</SelectItem>
                    <SelectItem value="adjustment">Ajuste</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Input type="number" min="0.01" step="0.01" value={row.qty} onChange={e => updateRow(i, { qty: parseFloat(e.target.value) || 0 })} className="h-8 text-xs" placeholder="Qtd" />
              </div>
              <div className="col-span-2">
                <Input value={row.reason} onChange={e => updateRow(i, { reason: e.target.value })} className="h-8 text-xs" placeholder="Motivo" />
              </div>
              <div className="col-span-1 flex justify-end">
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive" onClick={() => removeRow(i)} disabled={rows.length === 1}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
          <Button size="sm" variant="outline" className="gap-1.5 w-full" onClick={addRow}>
            <Plus className="h-3.5 w-3.5" />
            Adicionar linha
          </Button>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? "Salvando..." : `Salvar ${rows.filter(r => r.qty > 0).length} movimentação(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Página Principal ─────────────────────────────────────────────────────────
export default function AdminProducts() {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [deleteProduct, setDeleteProductState] = useState<Product | null>(null);
  const [movementProduct, setMovementProduct] = useState<Product | null>(null);
  const [movementTab, setMovementTab] = useState("all");
  const [usageProduct, setUsageProduct] = useState<Product | null>(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [movPeriodStart, setMovPeriodStart] = useState("");
  const [movPeriodEnd, setMovPeriodEnd] = useState("");
  const [activeTab, setActiveTab] = useState("products");

  const utils = trpc.useUtils();

  const { data: productList = [], isLoading } = trpc.products.list.useQuery({
    search: search || undefined,
    category: categoryFilter !== "all" ? categoryFilter : undefined,
    includeInactive: false,
  });

  // Stable query inputs — objetos literais inline causam re-fetches infinitos
  const lowStockListInput = useMemo(() => ({ lowStock: true as const }), []);
  const movementsInput = useMemo(() => ({ limit: 100 }), []);
  const { data: lowStockList = [] } = trpc.products.list.useQuery(lowStockListInput);
  const { data: movements = [] } = trpc.products.getMovements.useQuery(movementsInput);
  const { data: salesReport = [] } = trpc.products.salesReport.useQuery({});
  const { data: inventoryData } = trpc.products.inventoryReport.useQuery();
  const purchaseSuggestionsInput = useMemo(() => ({ days: 30, coverageDays: 21 }), []);
  const { data: purchaseSuggestions = [] } = trpc.products.purchaseSuggestions.useQuery(purchaseSuggestionsInput);
  const { data: usageReport = [] } = trpc.products.usageReport.useQuery({});
  const usageByProfessional = useMemo(() => Object.values(usageReport.reduce((acc: Record<string, { name: string; quantity: number; cost: number; entries: number }>, item: any) => {
    const key = String(item.professionalId ?? "unassigned");
    const current = acc[key] ?? { name: item.professionalName ?? "Sem profissional", quantity: 0, cost: 0, entries: 0 };
    current.quantity += Math.abs(Number(item.qty ?? 0));
    current.cost += Math.abs(Number(item.qty ?? 0)) * Number(item.unitCost ?? 0);
    current.entries += 1;
    acc[key] = current;
    return acc;
  }, {})).sort((a, b) => b.cost - a.cost), [usageReport]);
  const usageByService = useMemo(() => Object.values(usageReport.reduce((acc: Record<string, { name: string; quantity: number; cost: number; entries: number }>, item: any) => {
    const key = String(item.serviceId ?? "unassigned");
    const current = acc[key] ?? { name: item.serviceName ?? "Uso próprio ou sem serviço", quantity: 0, cost: 0, entries: 0 };
    current.quantity += Math.abs(Number(item.qty ?? 0));
    current.cost += Math.abs(Number(item.qty ?? 0)) * Number(item.unitCost ?? 0);
    current.entries += 1;
    acc[key] = current;
    return acc;
  }, {})).sort((a, b) => b.cost - a.cost), [usageReport]);
  const usageSummary = useMemo(() => {
    const totalCost = usageReport.reduce((total: number, item: any) => total + Math.abs(Number(item.qty ?? 0)) * Number(item.unitCost ?? 0), 0);
    return {
      totalCost,
      totalEntries: usageReport.length,
      topProfessional: usageByProfessional[0] ?? null,
      topService: usageByService[0] ?? null,
    };
  }, [usageReport, usageByProfessional, usageByService]);

  const bulkMovementMutation = trpc.products.bulkAddMovement.useMutation({
    onSuccess: (r) => {
      toast.success(`${r.count} movimentação(ões) registrada(s)!`);
      utils.products.list.invalidate();
      utils.products.getMovements.invalidate();
      utils.products.inventoryReport.invalidate();
      setShowBulkModal(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const createMutation = trpc.products.create.useMutation({
    onSuccess: () => {
      toast.success("Produto cadastrado com sucesso!");
      utils.products.list.invalidate();
      setShowCreateModal(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const updateMutation = trpc.products.update.useMutation({
    onSuccess: () => {
      toast.success("Produto atualizado!");
      utils.products.list.invalidate();
      setEditProduct(null);
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = trpc.products.delete.useMutation({
    onSuccess: () => {
      toast.success("Produto removido!");
      utils.products.list.invalidate();
      setDeleteProductState(null);
    },
    onError: (e) => toast.error(e.message),
  });

  const totalValue = productList.reduce((acc, p) => acc + parseFloat(p.stockQty) * parseFloat(p.salePrice), 0);
  const totalCostValue = productList.reduce((acc, p) => acc + parseFloat(p.stockQty) * parseFloat(p.costPrice), 0);
  const filteredMovements = (() => {
    let list = movementTab === "all" ? movements : movements.filter(m => m.type === movementTab);
    if (movPeriodStart) list = list.filter(m => new Date(m.createdAt) >= new Date(movPeriodStart));
    if (movPeriodEnd) list = list.filter(m => new Date(m.createdAt) <= new Date(movPeriodEnd + "T23:59:59"));
    return list;
  })();

  const exportInventoryCSV = () => {
    if (!inventoryData) return;
    const header = "Nome,Categoria,Marca,SKU,Unidade,Qtd Estoque,Estoque Mín,Custo Unit.,Preço Venda,Valor Custo Total,Valor Venda Total,Status";
    const rows = inventoryData.items.map(i =>
      `"${i.name}","${i.category}","${i.brand}","${i.sku}","${i.unit}",${i.stockQty},${i.stockMin},${i.costPrice.toFixed(2)},${i.salePrice.toFixed(2)},${i.totalCostValue.toFixed(2)},${i.totalSaleValue.toFixed(2)},"${i.isOutOfStock ? "Sem estoque" : i.isLowStock ? "Estoque baixo" : "OK"}"`
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `inventario_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const exportRestockCSV = () => {
    if (!inventoryData) return;
    const lowStockItems = inventoryData.items.filter((item) => item.isLowStock || item.isOutOfStock);
    if (lowStockItems.length === 0) {
      toast.info("Não há produtos com reposição pendente.");
      return;
    }
    const header = "Produto,Categoria,SKU,Unidade,Estoque Atual,Estoque Mínimo,Reposição Sugerida,Status";
    const rows = lowStockItems.map((item) => {
      const suggestedQty = Math.max(item.stockMin - item.stockQty, 0);
      const status = item.isOutOfStock ? "Sem estoque" : "Estoque baixo";
      const escape = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;
      return [escape(item.name), escape(item.category), escape(item.sku), escape(item.unit), item.stockQty, item.stockMin, suggestedQty, escape(status)].join(",");
    });
    const blob = new Blob(["\uFEFF" + [header, ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `reposicao_estoque_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Produtos
          </h1>
          <p className="text-sm text-muted-foreground">Gerencie seu catálogo de produtos, estoque e preços</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="gap-2 shrink-0">
          <Plus className="h-4 w-4" />
          Novo Produto
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <Boxes className="h-3.5 w-3.5" />
              Total de Produtos
            </div>
            <p className="text-2xl font-bold">{productList.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-orange-400 text-xs mb-1">
              <AlertTriangle className="h-3.5 w-3.5" />
              Estoque Baixo
            </div>
            <p className="text-2xl font-bold text-orange-400">{lowStockList.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <TrendingUp className="h-3.5 w-3.5" />
              Valor em Estoque
            </div>
            <p className="text-lg font-bold">{fmtCurrency(totalValue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
              <ShoppingBag className="h-3.5 w-3.5" />
              Custo em Estoque
            </div>
            <p className="text-lg font-bold">{fmtCurrency(totalCostValue)}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Mobile: caixa suspensa */}
        <div className="block md:hidden mb-2">
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Selecione a seção" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="products">Produtos</SelectItem>
              <SelectItem value="movements">Movimentações</SelectItem>
              <SelectItem value="report">Relatório de Vendas</SelectItem>
              <SelectItem value="inventory">Inventário</SelectItem>
              <SelectItem value="restock">Reposição inteligente</SelectItem>
              <SelectItem value="usage">Consumo inteligente</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Desktop: abas normais */}
        <TabsList className="hidden md:inline-flex">
          <TabsTrigger value="products">Produtos</TabsTrigger>
          <TabsTrigger value="movements">
            Movimentações
            {movements.length > 0 && <Badge variant="secondary" className="ml-1.5 text-xs">{movements.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="report">Relatório de Vendas</TabsTrigger>
          <TabsTrigger value="inventory">
            <BookOpen className="h-3.5 w-3.5 mr-1" />
            Inventário
          </TabsTrigger>
          <TabsTrigger value="restock">
            <PackagePlus className="h-3.5 w-3.5 mr-1" />
            Reposição inteligente
            {purchaseSuggestions.length > 0 && <Badge variant="secondary" className="ml-1.5 text-xs">{purchaseSuggestions.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="usage">
            <Scissors className="h-3.5 w-3.5 mr-1" />
            Consumo inteligente
          </TabsTrigger>
        </TabsList>

        <TabsContent value="restock" className="space-y-4 mt-4">
          <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
            <CardContent className="flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
              <div><div className="flex items-center gap-2"><Calculator className="h-5 w-5 text-amber-700" /><h2 className="font-bold text-amber-950">Sugestão de compra inteligente</h2></div><p className="mt-2 max-w-2xl text-sm leading-6 text-amber-900">A prioridade combina estoque mínimo e o giro dos últimos 30 dias. A quantidade sugerida busca manter até 21 dias de cobertura, sem criar pedidos automaticamente.</p></div>
              <Badge className="w-fit border-amber-200 bg-white text-amber-800 hover:bg-white">{purchaseSuggestions.length} item(ns) para revisar</Badge>
            </CardContent>
          </Card>
          {purchaseSuggestions.length === 0 ? <Card><CardContent className="p-8 text-center"><CheckCircle2 className="mx-auto h-9 w-9 text-emerald-600" /><p className="mt-3 font-semibold">Estoque sob controle</p><p className="mt-1 text-sm text-muted-foreground">Nenhum produto exige reposição com as regras atuais.</p></CardContent></Card> : <div className="space-y-3">{purchaseSuggestions.map((item) => <Card key={item.id} className={item.priority === "critical" ? "border-rose-200" : item.priority === "high" ? "border-amber-200" : ""}><CardContent className="flex flex-col gap-4 p-4 md:flex-row md:items-center md:justify-between"><div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><p className="font-semibold">{item.name}</p><Badge variant={item.priority === "critical" ? "destructive" : "secondary"}>{item.priority === "critical" ? "Sem estoque" : item.priority === "high" ? "Estoque baixo" : "Acompanhar"}</Badge></div><p className="mt-1 text-sm text-muted-foreground">{item.category} · Atual: {item.stockQty} {item.unit} · Mínimo: {item.stockMin} {item.unit}{item.estimatedCoverageDays !== null ? ` · Cobertura: ${item.estimatedCoverageDays} dia(s)` : ""}</p></div><div className="grid grid-cols-2 gap-4 text-right"><div><p className="text-xs text-muted-foreground">Sugestão</p><p className="font-bold text-primary">+{item.suggestedQty} {item.unit}</p></div><div><p className="text-xs text-muted-foreground">Custo estimado</p><p className="font-bold">{fmtCurrency(item.estimatedCost)}</p></div></div></CardContent></Card>)}</div>}
        </TabsContent>

        <TabsContent value="usage" className="space-y-4 mt-4">
          <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-white">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-violet-100 p-2"><Scissors className="h-5 w-5 text-violet-700" /></div>
                <div><h2 className="font-bold text-violet-950">Consumo por profissional e serviço</h2><p className="mt-1 text-sm text-violet-900">Acompanhe os produtos descontados em comissão e os usados nos atendimentos. O uso interno deve ser associado ao serviço para compor a rentabilidade.</p></div>
              </div>
            </CardContent>
          </Card>
          {usageReport.length > 0 && <div className="grid gap-3 rounded-xl border border-violet-200 bg-violet-50/50 p-3 sm:grid-cols-2 lg:grid-cols-4"><div><p className="text-[11px] text-muted-foreground">Custo consumido</p><p className="mt-1 font-bold text-violet-700">{fmtCurrency(usageSummary.totalCost)}</p></div><div><p className="text-[11px] text-muted-foreground">Lançamentos</p><p className="mt-1 font-bold">{usageSummary.totalEntries}</p></div><div><p className="text-[11px] text-muted-foreground">Maior consumo profissional</p><p className="mt-1 truncate font-bold">{usageSummary.topProfessional?.name ?? "—"}</p><p className="text-[11px] text-muted-foreground">{usageSummary.topProfessional ? fmtCurrency(usageSummary.topProfessional.cost) : "Sem dados"}</p></div><div><p className="text-[11px] text-muted-foreground">Serviço com maior consumo</p><p className="mt-1 truncate font-bold">{usageSummary.topService?.name ?? "—"}</p><p className="text-[11px] text-muted-foreground">{usageSummary.topService ? fmtCurrency(usageSummary.topService.cost) : "Sem dados"}</p></div></div>}
          {usageReport.length === 0 ? <Card><CardContent className="p-8 text-center"><Scissors className="mx-auto h-9 w-9 text-muted-foreground" /><p className="mt-3 font-semibold">Nenhum consumo registrado neste período</p><p className="mt-1 text-sm text-muted-foreground">Registre o uso no produto ou durante o pagamento do atendimento para alimentar esta visão.</p></CardContent></Card> : <div className="grid gap-4 lg:grid-cols-2"><Card><CardHeader><CardTitle className="text-base">Por profissional</CardTitle><CardDescription>Valor de produtos debitado da comissão.</CardDescription></CardHeader><CardContent className="space-y-3">{usageByProfessional.map((item) => <div key={item.name} className="flex items-center justify-between gap-3 rounded-lg border p-3"><div><p className="font-medium">{item.name}</p><p className="text-xs text-muted-foreground">{item.entries} lançamento(s) · {item.quantity.toFixed(2)} un</p></div><p className="font-bold text-amber-700">-{fmtCurrency(item.cost)}</p></div>)}</CardContent></Card><Card><CardHeader><CardTitle className="text-base">Por serviço</CardTitle><CardDescription>Produtos usados na execução de cada serviço.</CardDescription></CardHeader><CardContent className="space-y-3">{usageByService.map((item) => <div key={item.name} className="flex items-center justify-between gap-3 rounded-lg border p-3"><div><p className="font-medium">{item.name}</p><p className="text-xs text-muted-foreground">{item.entries} lançamento(s) · {item.quantity.toFixed(2)} un</p></div><p className="font-bold text-violet-700">{fmtCurrency(item.cost)}</p></div>)}</CardContent></Card></div>}
        </TabsContent>

        {/* Aba Produtos */}
        <TabsContent value="products" className="space-y-4 mt-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar produto..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as categorias</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-48 rounded-lg bg-muted/50 animate-pulse" />
              ))}
            </div>
          ) : productList.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Nenhum produto cadastrado</p>
              <p className="text-sm mt-1">Clique em "Novo Produto" para começar</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {productList.map(product => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onEdit={() => setEditProduct(product)}
                  onDelete={() => setDeleteProductState(product)}
                  onMovement={() => setMovementProduct(product)}
                  onUsage={() => setUsageProduct(product)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Aba Movimentações */}
        <TabsContent value="movements" className="space-y-4 mt-4">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              {(["all", "in", "out", "adjustment"] as const).map(t => (
              <Button
                key={t}
                size="sm"
                variant={movementTab === t ? "default" : "outline"}
                onClick={() => setMovementTab(t)}
                className="text-xs"
              >
                {t === "all" ? "Todas" : t === "in" ? "Entradas" : t === "out" ? "Saídas" : "Ajustes"}
              </Button>
            ))}
            </div>
            <div className="flex gap-2 items-center text-sm">
              <Filter className="h-3.5 w-3.5 text-muted-foreground" />
              <input type="date" value={movPeriodStart} onChange={e => setMovPeriodStart(e.target.value)}
                className="h-8 px-2 rounded border border-input bg-background text-xs" />
              <span className="text-muted-foreground">até</span>
              <input type="date" value={movPeriodEnd} onChange={e => setMovPeriodEnd(e.target.value)}
                className="h-8 px-2 rounded border border-input bg-background text-xs" />
              {(movPeriodStart || movPeriodEnd) && (
                <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => { setMovPeriodStart(""); setMovPeriodEnd(""); }}>
                  <XCircle className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>

          {filteredMovements.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ArrowUpDown className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>Nenhuma movimentação registrada</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMovements.map(m => (
                <div key={m.id} className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${
                    m.type === "in" ? "bg-green-500/10" : m.type === "out" ? "bg-red-500/10" : "bg-yellow-500/10"
                  }`}>
                    {m.type === "in" ? (
                      <TrendingUp className="h-4 w-4 text-green-400" />
                    ) : m.type === "out" ? (
                      <TrendingDown className="h-4 w-4 text-red-400" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-yellow-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {m.reason ?? (m.type === "in" ? "Entrada" : m.type === "out" ? "Saída" : "Ajuste")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(m.createdAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      {m.notes && ` · ${m.notes}`}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-sm font-semibold ${m.type === "in" ? "text-green-400" : m.type === "out" ? "text-red-400" : "text-yellow-400"}`}>
                      {m.type === "out" ? "-" : "+"}{fmt(m.qty)}
                    </p>
                    {m.type === "out" && m.unitPrice && (
                      <p className="text-xs text-muted-foreground">{fmtCurrency(parseFloat(m.qty) * parseFloat(m.unitPrice))}</p>
                    )}
                    {m.type === "in" && m.unitCost && (
                      <p className="text-xs text-muted-foreground">{fmtCurrency(parseFloat(m.qty) * parseFloat(m.unitCost))}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Aba Inventário */}
        <TabsContent value="inventory" className="space-y-4 mt-4">
          {!inventoryData ? (
            <div className="text-center py-12 text-muted-foreground">
              <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-30 animate-pulse" />
              <p>Carregando inventário...</p>
            </div>
          ) : (
            <>
              {/* KPIs do inventário */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card><CardContent className="p-4">
                  <p className="text-xs text-muted-foreground mb-1">Valor de Custo</p>
                  <p className="text-lg font-bold">{fmtCurrency(inventoryData.totalCost)}</p>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                  <p className="text-xs text-muted-foreground mb-1">Valor de Venda</p>
                  <p className="text-lg font-bold text-green-400">{fmtCurrency(inventoryData.totalSale)}</p>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                  <p className="text-xs text-orange-400 mb-1">Estoque Baixo</p>
                  <p className="text-2xl font-bold text-orange-400">{inventoryData.lowStockCount}</p>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                  <p className="text-xs text-destructive mb-1">Sem Estoque</p>
                  <p className="text-2xl font-bold text-destructive">{inventoryData.outOfStockCount}</p>
                </CardContent></Card>
              </div>
              {/* Ações */}
              <div className="flex gap-2 justify-between items-center">
                <p className="text-xs text-muted-foreground">
                  Gerado em {new Date(inventoryData.generatedAt).toLocaleString("pt-BR")} · {inventoryData.items.length} produtos
                </p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setShowBulkModal(true)}>
                    <PackagePlus className="h-3.5 w-3.5" />
                    Entrada em Lote
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={exportInventoryCSV}>
                    <Download className="h-3.5 w-3.5" />
                    Exportar CSV
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5 border-orange-300 text-orange-700 hover:bg-orange-50" onClick={exportRestockCSV}>
                    <ShoppingBag className="h-3.5 w-3.5" />
                    Lista de Reposição
                  </Button>
                </div>
              </div>
              {/* Tabela de inventário */}
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium">Produto</th>
                      <th className="text-left p-3 font-medium hidden sm:table-cell">Categoria</th>
                      <th className="text-right p-3 font-medium">Qtd</th>
                      <th className="text-right p-3 font-medium hidden md:table-cell">Custo Unit.</th>
                      <th className="text-right p-3 font-medium hidden md:table-cell">Preço Venda</th>
                      <th className="text-right p-3 font-medium">Valor Total</th>
                      <th className="text-center p-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {inventoryData.items.map(item => (
                      <tr key={item.id} className="hover:bg-muted/30 transition-colors">
                        <td className="p-3">
                          <p className="font-medium">{item.name}</p>
                          {item.sku && <p className="text-xs text-muted-foreground">SKU: {item.sku}</p>}
                        </td>
                        <td className="p-3 text-muted-foreground hidden sm:table-cell">{item.category}</td>
                        <td className="p-3 text-right font-medium">
                          {item.stockQty.toFixed(2)} {item.unit}
                          {item.stockMin > 0 && <p className="text-xs text-muted-foreground">mín: {item.stockMin}</p>}
                        </td>
                        <td className="p-3 text-right text-muted-foreground hidden md:table-cell">{fmtCurrency(item.costPrice)}</td>
                        <td className="p-3 text-right text-muted-foreground hidden md:table-cell">{fmtCurrency(item.salePrice)}</td>
                        <td className="p-3 text-right">
                          <p className="font-semibold">{fmtCurrency(item.totalSaleValue)}</p>
                          <p className="text-xs text-muted-foreground">custo: {fmtCurrency(item.totalCostValue)}</p>
                        </td>
                        <td className="p-3 text-center">
                          {item.isOutOfStock ? (
                            <Badge variant="destructive" className="text-xs">Sem estoque</Badge>
                          ) : item.isLowStock ? (
                            <Badge className="text-xs bg-orange-500/20 text-orange-400 border-orange-500/30">Baixo</Badge>
                          ) : (
                            <CheckCircle2 className="h-4 w-4 text-green-400 mx-auto" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-muted/30 font-semibold">
                    <tr>
                      <td colSpan={5} className="p-3 text-right hidden md:table-cell">Total Geral:</td>
                      <td colSpan={3} className="p-3 text-right md:hidden">Total:</td>
                      <td className="p-3 text-right">
                        <p>{fmtCurrency(inventoryData.totalSale)}</p>
                        <p className="text-xs font-normal text-muted-foreground">custo: {fmtCurrency(inventoryData.totalCost)}</p>
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </TabsContent>

        {/* Aba Relatório de Vendas */}
        <TabsContent value="report" className="space-y-4 mt-4">
          {salesReport.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <BarChart3 className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>Nenhuma venda registrada ainda</p>
              <p className="text-sm mt-1">As vendas aparecerão aqui ao registrar saídas de estoque</p>
            </div>
          ) : (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Top Produtos — Mês Atual</h3>
              {salesReport.map((item, i) => (
                <div key={item.productId} className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                  <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.productName}</p>
                    {item.category && <p className="text-xs text-muted-foreground">{item.category}</p>}
                  </div>
                  <div className="text-right shrink-0 space-y-0.5">
                    <p className="text-sm font-semibold text-green-400">{fmtCurrency(item.totalRevenue)}</p>
                    <p className="text-xs text-muted-foreground">{parseFloat(String(item.totalQty)).toFixed(2)} un vendidas</p>
                    <p className="text-xs text-primary">{parseFloat(String(item.avgMargin)).toFixed(1)}% margem</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modal Criar Produto */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Novo Produto
            </DialogTitle>
          </DialogHeader>
          <ProductForm
            onSave={data => createMutation.mutate(data)}
            onCancel={() => setShowCreateModal(false)}
            isLoading={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Modal Editar Produto */}
      {editProduct && (
        <Dialog open onOpenChange={() => setEditProduct(null)}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit2 className="h-5 w-5" />
                Editar Produto
              </DialogTitle>
            </DialogHeader>
            <ProductForm
              initial={editProduct}
              onSave={data => updateMutation.mutate({ id: editProduct.id, ...data })}
              onCancel={() => setEditProduct(null)}
              isLoading={updateMutation.isPending}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Modal Movimentação de Estoque */}
      {movementProduct && (
        <StockMovementModal
          product={movementProduct}
          onClose={() => setMovementProduct(null)}
        />
      )}

      {/* Modal Entrada em Lote */}
      {showBulkModal && (
        <BulkMovementModal
          products={productList}
          onClose={() => setShowBulkModal(false)}
          onSave={(movements) => bulkMovementMutation.mutate({ movements })}
          isLoading={bulkMovementMutation.isPending}
        />
      )}

      {/* Modal Registrar Uso de Produto */}
      {usageProduct && (
        <RegisterUsageModal
          fixedProductId={usageProduct.id}
          fixedProductName={usageProduct.name}
          onClose={() => setUsageProduct(null)}
          onSuccess={() => {
            utils.products.list.invalidate();
            utils.products.getMovements.invalidate();
          }}
        />
      )}

      {/* AlertDialog Excluir */}
      <AlertDialog open={!!deleteProduct} onOpenChange={() => setDeleteProductState(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover produto?</AlertDialogTitle>
            <AlertDialogDescription>
              O produto <strong>{deleteProduct?.name}</strong> será desativado. O histórico de movimentações será mantido.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => deleteProduct && deleteMutation.mutate({ id: deleteProduct.id })}
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
